import { useAuth } from '@clerk/clerk-react';
import type { Source } from '@ditto/shared';
import {
  skipToken,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  useAuthenticatedClient,
  useProtectedQuery,
  useWorkspaceBaseUrl,
} from './useApi';

const queryKeys = {
  all: ['sources'],
  sources: () => [...queryKeys.all, 'sources'],
  knowledgeGroups: () => [...queryKeys.all, 'knowledge-groups'],
};

export const useSourcesApi = (options?: {
  refetchInterval?: UseQueryOptions<
    Source[],
    Error,
    Source[],
    unknown[]
  >['refetchInterval'];
}) => {
  const { client } = useAuthenticatedClient();

  const queryKey = queryKeys.sources();

  return {
    queryKey,
    ...useProtectedQuery({
      queryKey,
      queryFn: async () => {
        const response = await client.sources.getSources();
        if (response.status === 200) {
          return response.body;
        }
        throw new Error('Failed to fetch sources');
      },
      ...options,
    }),
  };
};

export const useKnowledgeGroupsApi = () => {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.knowledgeGroups(),
    queryFn: async () => {
      const response = await client.sources.getKnowledgeGroups();
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch knowledge groups');
    },
  });
};

// New API hooks for bulk S3 upload using ts-rest contracts
export const useGetPresignedUrlsApi = () => {
  const { client } = useAuthenticatedClient();

  return useMutation({
    mutationFn: async (
      files: Array<{ name: string; type: string; size: number }>
    ) => {
      const response = await client.sources.getPresignedUrls({
        body: { files },
      });

      if (response.status === 200) {
        return response.body.uploadUrls;
      }

      throw new Error('Failed to get presigned URLs');
    },
    onError: (error: Error) => {
      console.error('Failed to get presigned URLs:', error);
    },
  });
};

export const useRegisterFilesApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      files,
    }: {
      files: Array<{
        s3Key: string;
        fileName: string;
        size: number;
        mimeType: string;
      }>;
    }) => {
      const response = await client.sources.registerFiles({
        body: { files },
      });

      if (response.status === 200) {
        return response.body;
      }

      throw new Error('Failed to register files');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.sources() });
    },
    onError: error => {
      console.error('Failed to register files:', error);
    },
  });
};

export const useDeleteSourceApi = () => {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await client.sources.deleteSource({ params: { id } });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to delete source');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sources() });
    },
  });
};

export const useDownloadSourceFileApi = (fileId: string | undefined) => {
  const { getToken } = useAuth();
  const workspaceBaseUrl = useWorkspaceBaseUrl();

  return useProtectedQuery({
    queryKey: ['download_source_file', fileId],
    queryFn: fileId
      ? async () => {
          const token = await getToken();
          const response = await fetch(
            `${workspaceBaseUrl}/sources/${fileId}/download`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Download failed');
          }

          return response.blob();
        }
      : skipToken,
  });
};
